<template>
  <div
    v-if="prelims"
    class="prelims-participants"
  >
    <div class="dancers-heading">
      <slot name="title">
        <!--        <h3 class="count">-->
        <!--          {{ prelims.participants.length }} Dancers-->
        <!--        </h3>-->
      </slot>

      <BKSort
        :sort-options="sortOptions"
        class="sort"
        @update="updateSort"
      />
    </div>

    <div class="search-wrapper">
      <BKInput
        v-model="searchText"
        :append-icon="mdiMagnify"
        label="Search dancers"
        @input="handleSearch"
      />
    </div>
    <div
      v-for="participant in filteredParticipants"
      :key="participant.id + participant.passStatus + finishKey"
      :class="['score', { highlighted: getIsParticipantHighlighted(participant) }, getCssStatusClass(participant)]"
    >
      <p
        v-if="participant.passStatus === 'sameScore' && !!participant.pointsAverage"
        ref="error"
        class="same-score-error-msg"
      >
        Too many competitors with the same points for the final bracket spot(s). Update the points on the competitors to
        proceed.
      </p>
      <PrelimsRoundScoreParticipants
        :battle-id="battle.id"
        :can-edit="prelims.permissions.canJudge"
        :can-set-gone="prelims.permissions.canSetGone"
        :event="event"
        :is-grayed-out="getIsParticipantGrayedOut(participant)"
        :is-highlighted="getIsParticipantHighlighted(participant)"
        :judgment="getParticipantJudgment(participant.id)"
        :participant="participant"
        @cancel="cancelEditParticipantJudgment(participant.id)"
        @edit="editParticipantJudgment(participant.id)"
        @gone="setGone"
        @update:prelims="updatePrelims"
      >
        <template #actions="{ isShowActions }">
          <slot
            :is-show-actions="isShowActions"
            :participant-id="participant.id"
            name="actions"
          />
        </template>
      </PrelimsRoundScoreParticipants>
    </div>

    <section class="actions">
      <BKButton
        v-if="prelims.permissions.canFinishPrelims"
        :disabled="!prelims.permissions.canShuffle"
        :loading="loadingShuffleBtn"
        color="var(--color-blue-800)"
        white-text
        @click="reshuffle"
      >
        Randomize
      </BKButton>
      <BKButton
        v-if="prelims.permissions.canFinishPrelims && $route.name !== 'Group'"
        ref="addBracketBtn"
        :disabled="prelims.permissions.canShuffle || !!prelims.permissions.error"
        :loading="loading"
        white-text
        @click="showAddToBracketsConfirmationDialog"
      >
        Add to bracket
      </BKButton>
      <BKButton
        v-if="prelims.permissions.canUndoBrackets"
        ref="addBracketBtn"
        :loading="loading"
        white-text
        @click="showUndoBracketsConfirmationDialog"
      >
        Undo bracket
      </BKButton>

      <span
        v-if="prelims.permissions.error"
        class="submit-text err"
      >
        {{ prelims.permissions.error }}
      </span>
    </section>
    <BKConfirmationDialog
      :confirm-text="confirmationDialog.confirmText"
      :is-show="confirmationDialog.isShow"
      :title="confirmationDialog.title"
      @cancel="confirmationDialog.isShow = false"
      @confirm="confirmationDialog.confirm"
    >
      {{ confirmationDialog.subtitle }}
    </BKConfirmationDialog>
  </div>
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import BKInput from '@/components/AppShared/BKInput.vue'
import { mdiMagnify } from '@mdi/js'
import { BKConfirmationDialog } from '@/shared/ui/modals'
import PrelimsRoundScoreParticipants from '@/components/Battle/Prelims/PrelimsRoundScoreParticipants.vue'

export default {
  name: 'BattlePrelims',
  components: {
    PrelimsRoundScoreParticipants,
    BKConfirmationDialog,
    BKInput,
  },
  props: {
    battle: {
      type: Object,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    groupId: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      mdiMagnify,
      filteredParticipants: [],
      loading: false,
      prelims: null,
      sortBy: '',
      searchText: '',
      participantsForJudgment: [],
      loadingShuffleBtn: false,
      sortOptions: [
        { name: 'Points', value: 'points' },
        { name: 'Name', value: 'name' },
        { name: 'Assigned number', value: 'number' },
      ],
      finishKey: 0,
      confirmationDialog: {
        isShow: false,
        title: '',
        subtitle: '',
        confirmText: '',
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        confirm: () => {},
      },
    }
  },
  computed: {
    ...mapGetters(['getUserDB']),
    isParticipantWithSameScore() {
      if (!this.prelims) return false
      return this.prelims.participants.some((p) => p.passStatus === 'sameScore')
    },
    isParticipantsWithStatus() {
      return this.prelims.participants.every((p) => p.passStatus)
    },
    isParticipantsWithoutScore() {
      return this.prelims.participants.some((p) => p.pointsAverage === 0)
    },
  },
  watch: {
    prelims: {
      handler() {
        this.handleSearch()
      },
      deep: true,
    },
    groupId() {
      this.handleSearch()
    },
  },
  async mounted() {
    await this.fetchPrelims()
    if (this.prelims.permissions.canJudge) this.setParticipantsForJudgment()
    if (this.isParticipantWithSameScore) this.scrollToErrorMsg()
  },
  methods: {
    ...mapActions(['setIsNotFound', 'showAlertToast']),
    getCssStatusClass(participant) {
      if (!participant) return ''
      const isCurrentUserAdmin = !!this.event.staff.admins.find((admin) => admin.id === this.getUserDB?.id)
      const isCurrentUserOrganizer = this.event.staff?.organizer?.id === this.getUserDB?.id
      if (
        this.getParticipantJudgment(participant.id)?.points ||
        participant.isPromoted ||
        isCurrentUserAdmin ||
        isCurrentUserOrganizer
      ) {
        return {
          'is-passed': participant.passStatus === 'passed',
          'is-samescore': participant.passStatus === 'sameScore',
        }
      }
      return ''
    },
    showUndoBracketsConfirmationDialog() {
      this.confirmationDialog = {
        isShow: true,
        title: 'Undo brackets',
        subtitle: `Are you sure you want to undo brackets?`,
        confirmText: 'Undo',
        confirm: () => {
          this.undoBrackets()
          this.confirmationDialog.isShow = false
        },
      }
    },
    showAddToBracketsConfirmationDialog() {
      this.confirmationDialog = {
        isShow: true,
        title: 'Add to bracket',
        subtitle: `Are you sure you want to add to brackets?`,
        confirmText: 'Add',
        confirm: () => {
          this.finishPrelims()
          this.confirmationDialog.isShow = false
        },
      }
    },
    async undoBrackets() {
      this.loading = true
      await api.prelims.undoBrackets(this.battle.id)
      await this.fetchPrelims()
      this.loading = false
      this.finishKey += 1
    },
    async reshuffle() {
      this.loadingShuffleBtn = true
      const updatedPrelims = await api.prelims.reshuffleParticipants(this.battle.id)
      this.loadingShuffleBtn = false
      if (!updatedPrelims) return
      this.prelims = updatedPrelims
    },
    updatePrelims(prelims) {
      this.prelims = prelims

      if (!this.isParticipantWithSameScore && this.isParticipantsWithStatus && !this.isParticipantsWithoutScore) {
        this.scrollToAddBracketBtn()
        this.showAlertToast('bracketingIsReady')
      } else {
        this.scrollToErrorMsg()
      }
    },
    updateSort(sortBy) {
      this.sortBy = sortBy
      this.fetchPrelims()
    },
    async fetchPrelims() {
      const prelims = await api.prelims.getPrelims(this.$route.params.id, this.sortBy)
      if (!prelims) {
        this.setIsNotFound(true)
        return
      }

      this.prelims = prelims
    },
    getIsParticipantHighlighted(participant) {
      if (!this.getUserDB) return
      // eslint-disable-next-line consistent-return
      return this.getUserDB.id === participant.id
    },
    getIsParticipantGrayedOut(participant) {
      if (this.battle.status === 'prelims-finished') return !participant.isPromoted
      return participant.isPromoted || participant.isGone
    },
    setParticipantsForJudgment() {
      this.participantsForJudgment = []
      if (!this.getUserDB) return
      this.prelims.participants.forEach((p) => {
        const found = p?.prelims?.rounds?.[0]?.find((j) => j.id === this.getUserDB.id && j.points)
        if (found) return

        this.participantsForJudgment.push({
          userId: p.id,
          points: 0,
          mode: 'new',
        })
      })
    },
    editParticipantJudgment(participantId) {
      const participant = this.prelims.participants.find((p) => p.id === participantId)
      if (!participant || !this.getUserDB) return

      const currentUserJudgment = participant?.prelims?.rounds?.[0]?.find((j) => j.id === this.getUserDB.id)
      if (!currentUserJudgment) return

      const participantForEdit = {
        userId: participant.id,
        points: currentUserJudgment.points,
        mode: 'edit',
      }

      this.participantsForJudgment.push(participantForEdit)
    },
    cancelEditParticipantJudgment(participantId) {
      this.participantsForJudgment = this.participantsForJudgment.filter((p) => p.userId !== participantId)
    },
    getParticipantJudgment(participantId) {
      const participant = this.prelims.participants.find((p) => p.id === participantId)
      if (!participant || !this.getUserDB) return null

      const currentUserJudgment = participant?.prelims?.rounds?.[0]?.find((j) => j.id === this.getUserDB.id)

      return {
        userId: participant.id,
        points: currentUserJudgment ? currentUserJudgment.points : null,
        mode: 'edit',
        round: 0,
      }
    },
    async finishPrelims() {
      this.loading = true

      const updatedPrelims = await api.prelims.finishPrelims(this.battle.id)
      this.loading = false
      if (!updatedPrelims) return
      this.finishKey += 1
      this.prelims = updatedPrelims

      if (this.isParticipantWithSameScore) this.scrollToErrorMsg()
    },
    async setGone(participantId) {
      const updatedPrelims = await api.prelims.setGone(this.battle.id, participantId)
      if (!updatedPrelims) return
      this.prelims = updatedPrelims
    },
    handleSearch() {
      if (this.groupId) {
        this.filteredParticipants = this.prelims.participants.filter((p) => {
          const name = p.dancerName.toLowerCase()
          const search = this.searchText.toLowerCase()
          return name.includes(search) && p.group.toLowerCase() === this.groupId.toLowerCase()
        })
        return
      }

      this.filteredParticipants = this.prelims.participants.filter((p) => {
        const name = p.dancerName.toLowerCase()
        const search = this.searchText.toLowerCase()
        return name.includes(search)
      })
    },
    scrollToErrorMsg() {
      this.$nextTick(() => {
        if (!this.$refs.error) return
        if (Array.isArray(this.$refs.error)) {
          const showingErrorMsg = this.$refs.error.find((item) => item.clientHeight)
          showingErrorMsg.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    scrollToAddBracketBtn() {
      this.$nextTick(() => {
        if (!this.$refs.addBracketBtn) return
        this.$refs.addBracketBtn.$el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/style/mixins';

.prelims-participants {
  position: relative;
  background-color: var(--color-grey-100) !important;

  .same-score-error-msg {
    display: none;
  }

  .score {
    border-bottom: 1px solid var(--color-grey-600);

    &.highlighted {
      border-bottom: 2px solid var(--color-orange-500);
    }

    &.is-passed {
      ::v-deep {
        .judging-row {
          background-color: #83eaba;
          border-color: #00bf70;
        }

        .average-text,
        .chevron-icon,
        .points-value {
          color: #00824c;
        }
      }
    }

    &.is-samescore {
      ::v-deep {
        .average-text,
        .chevron-icon,
        .points-value {
          color: var(--rasberry-red);
        }
      }
    }

    &.is-samescore .same-score-error-msg {
      margin-bottom: 8px;
      padding: 40px 20px 0;
      color: var(--rasberry-red);
    }
  }

  & :not(.is-samescore) + .is-samescore .same-score-error-msg {
    display: block;
  }
}

.dancers-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 0 20px;

  .count {
    @include font-h3;
  }

  .sort {
    margin-left: auto;
  }
}

.search-wrapper {
  margin-bottom: 20px;
  padding: 0 20px;
}

.submit-text {
  color: var(--color-grey-300);

  &.err {
    color: #ff2d53;
  }
}

.actions {
  display: grid;
  margin: 40px 0 0;
  padding: 0 20px;
  row-gap: 10px;

  &:empty {
    display: none;
  }
}
</style>
